import { createContext, useContext, useEffect, useState } from "react";

import { useMediaQuery } from "../foundations/useMediaQuery.js";

/**
 * A side dock's layouts, by the screen's width. The layout itself is CSS (`SideDock`), right from
 * the first paint; these only tell a page which one it is in.
 */

/** From md (768px), where the phone's bars give way to the top nav: the dock floats, with its tab. */
export const SIDE_DOCK_FLOATS = "(min-width: 48rem)";

/** From 1441px: docked beside the page, which keeps the dock's room clear and slides beside it. */
export const SIDE_DOCK_DOCKS = "(min-width: 90.0625rem)";

/**
 * `docked` beside the page (from 1441px), `floating` over the page's edge (md to 1440px), or
 * `full`, filling the space between the phone's top bar and tab bar.
 */
export type SideDockMode = "docked" | "floating" | "full";

/** The dock's layout on this screen, kept current (`full` on the server). */
export function useSideDockMode(): SideDockMode {
  const floats = useMediaQuery(SIDE_DOCK_FLOATS);
  const docks = useMediaQuery(SIDE_DOCK_DOCKS);
  if (docks) return "docked";
  return floats ? "floating" : "full";
}

/** The page edge a dock sits on. */
export type SideDockSide = "left" | "right";

/** What `SideDockRoot` hands its parts. */
export interface SideDockContextValue {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  side: SideDockSide;
  /** The panel's content's id, for the tab's and a trigger's `aria-controls`. */
  contentId: string;
}

export const SideDockContext = createContext<SideDockContextValue | null>(null);

/** The nearest `SideDockRoot`'s state. Its parts and a page's own controls read it. */
export function useSideDock(): SideDockContextValue {
  return requireSideDock(useContext(SideDockContext));
}

function requireSideDock(value: SideDockContextValue | null): SideDockContextValue {
  if (value === null) throw new Error("Side dock parts go inside a SideDockRoot.");
  return value;
}

/**
 * True from the render where `value` changes until two frames later: the panel turns its
 * transitions off while the screen crosses a breakpoint where its place or open state changes.
 */
export function useInstantWhileChanging(value: boolean): boolean {
  const [seen, setSeen] = useState(value);
  const [instant, setInstant] = useState(false);
  if (seen !== value) {
    setSeen(value);
    setInstant(true);
  }
  useEffect(() => {
    if (!instant) return;
    let frame = requestAnimationFrame(() => {
      frame = requestAnimationFrame(() => setInstant(false));
    });
    return () => cancelAnimationFrame(frame);
  }, [instant]);
  return instant;
}

/** The variable the tab's place is read from: its middle, in px above the panel's foot. */
export const SIDE_DOCK_TAB_MIDDLE = "--side-dock-tab-middle";

/**
 * Puts the tab's middle `middle` px above the panel's foot, written straight onto the panel, so
 * the tab follows a row it lines up with (a chat's online row as its composer grows) without a
 * render. `null` (not mounted yet) does nothing.
 */
export function placeSideDockTab(panel: HTMLElement | null, middle: number): void {
  panel?.style.setProperty(SIDE_DOCK_TAB_MIDDLE, `${middle}px`);
}
